<template>
    <div>
        <!-- Display mobile widgets as a basic list -->
        <div v-if="isMobile" class="vrm-gridster vrm-gridster-mobile">
            <div v-for="w in layout" :key="w.i" :ref="getWidgetRef(w)">
                <vrm-widget
                    :widget="w"
                    :installation="installation"
                    :range="range"
                    :zoomstate="zoomstate"
                    :is-mobile="isMobile"
                    :graph-data="graphData[w.instance]"
                    :custom-graph-data="customGraphData[w.idCustomWidget]"
                    :loading="loading"
                    :has-touch-enabled="hasTouchEnabled"
                ></vrm-widget>
            </div>
        </div>

        <grid-layout
            v-else
            class="vrm-gridster"
            :class="{
                'vrm-gridster-touch': hasTouchEnabled,
                'vrm-gridster-locked': !gridEditable,
            }"
            :layout.sync="layout"
            :row-height="ROW_HEIGHT"
            :is-draggable="gridEditable"
            :is-resizable="gridEditable"
            :is-mirrored="false"
            :is-bounded="true"
            :vertical-compact="true"
            :margin="[10, 10]"
            :use-css-transforms="false"
            :col-num="3"
        >
            <vrm-widget-grid-item
                v-for="w in layout"
                :key="w.i"
                :widget="w"
                :installation="installation"
                :range="range"
                :zoomstate="zoomstate"
                :is-mobile="isMobile"
                :graph-data="graphData[w.instance]"
                :custom-graph-data="customGraphData[w.idCustomWidget]"
                :loading="loading"
                :has-touch-enabled="hasTouchEnabled"
                :persistent-mode="persistentMode"
                @resized="onWidgetModification"
                @moved="onWidgetModification"
                @widget-disable="onWidgetDisable"
            />
        </grid-layout>
    </div>
</template>

<script>
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { GridLayout } from 'vue-grid-layout';
import { AngularDirective } from '@/vue/decorators/angular-interop';
import { WIDGET_CODE_TO_TYPE } from '@/vue/components/shared/widget/codeToType';
import VrmWidgetGridItem from '@/vue/components/shared/grid/VrmWidgetGridItem.vue';
import VrmWidget from '@/vue/components/shared/grid/VrmWidget.vue';

const BREAKPOINT_MD = 'md';
const BREAKPOINT_SM = 'sm';
const ROW_HEIGHT = 105;
const MAX_ROWS = 240;

@Component({
    components: {
        VrmWidget,
        VrmWidgetGridItem,
        GridLayout,
    },
})
@AngularDirective('vrm-widget-grid')
export default class VrmWidgetGrid extends Vue {
    ROW_HEIGHT = ROW_HEIGHT;

    @Prop({ type: Array, default: () => [] })
    widgets;
    @Prop({ type: Object, default: null })
    range;
    @Prop({ type: Object, default: null })
    zoomstate;
    @Prop({ type: Object, default: null })
    installation;
    @Prop({ type: Object, default: () => {} })
    graphData;
    @Prop({ type: Object, default: () => {} })
    customGraphData;
    @Prop({ type: Boolean, default: true })
    loading;
    @Prop({ type: Boolean, default: false })
    locked;
    @Prop({ type: String, default: '' })
    searchTerm;
    @Prop({ type: Boolean, default: false })
    persistentMode;

    breakpoints = {
        md: 960,
        sm: 0,
    };

    breakpoint = BREAKPOINT_MD;
    expansion = {
        active: false,
        scrollY: 0,
        oldPositions: [],
        widget: -1,
    };
    layoutHash = null;
    layout = [];

    initial = true;

    @Watch('widgetIds', {
        immediate: true,
    })
    updateLayouts() {
        this.layout = this.parseLayout(this.widgets);

        this.$emit('sync-tooltips', this.$el);
        this.$emit('grid-full', this.findEmptyPosition(this.layout).y > MAX_ROWS);

        this.initial = false;
        this.layoutHash = this.generateLayoutPositionHash(this.layoutPositions(this.layout));
    }

    @Watch('widgetsLength')
    onWidgetsLengthChanged() {
        const positions = this.layoutPositions(this.layout);
        this.$emit('grid-changed', positions);
    }

    created() {
        window.addEventListener('resize', this.onResize);
        this.onResize();
    }

    mounted() {
        this.$emit('sync-tooltips', this.$el);
    }

    beforeDestroy() {
        window.removeEventListener('resize', this.onResize);
    }

    onResize(e) {
        if (e) e.preventDefault();

        const prevBp = this.breakpoint;
        const width = window.innerWidth;
        for (let bp in this.breakpoints) {
            if (width < this.breakpoints[bp]) continue;

            // Shrink any expanded widgets when breakpoints change to prevent issues with scrolling and
            // different expanded layouts for widgets depending on screen size.
            if (bp !== prevBp && this.expansion.active) this.shrinkWidget();
            this.breakpoint = bp;
            return;
        }
    }

    onWidgetModification() {
        this.onGridModification(true);
    }

    /**
     * Update layout positions when grid data changed.
     *
     * @param emit - Determines whether to emit a grid-changed event. Can be used to prevent grid changes from being
     * saved for mobile layouts while still having the grid redraw so charts update to new configs.
     */
    onGridModification(emit = true) {
        if (!this.gridEditable) return;

        const positions = this.layoutPositions(this.layout);
        const newLayoutHash = this.generateLayoutPositionHash(positions);
        if (this.layoutHash === newLayoutHash) return;

        if (this.setLayoutTimer) clearTimeout(this.setLayoutTimer);
        this.setLayoutTimer = setTimeout(() => {
            this.layoutHash = newLayoutHash;

            if (emit) {
                this.$emit('grid-changed', positions);
            }
        }, 500);
    }

    onWidgetDisable(e) {
        this.$emit('widget-disable', e);
    }

    sortLayout(layout) {
        return layout.sort((a, b) => {
            // Order by y, then x.
            const yDifference = a.y - b.y;
            if (yDifference !== 0) return yDifference;

            const xDifference = a.x - b.x;
            if (xDifference !== 0) return xDifference;

            return 0;
        });
    }

    parseLayout(angularWidgets) {
        const mapped = [];
        const unplaced = [];

        angularWidgets.forEach((w, i) => {
            const noPosition = !('col' in w) || !('row' in w) || !('sizeX' in w) || !('sizeY' in w);
            const inLayout = this.layout.find(l => l.uniqueIdentifier === w.uniqueIdentifier);
            const alreadyPlaced = inLayout !== undefined;

            if (!inLayout && noPosition) {
                unplaced.push(w);
                return;
            }

            const width = alreadyPlaced ? inLayout.w : w.sizeX;
            const height = alreadyPlaced ? inLayout.h : w.sizeY;
            mapped.push({
                ...w,
                i,
                x: alreadyPlaced ? inLayout.x : w.col,
                y: alreadyPlaced ? inLayout.y : w.row,
                w: width,
                h: height,
                type: WIDGET_CODE_TO_TYPE[w.code],
                gridItemState: {
                    active: true,
                    expanded: alreadyPlaced ? inLayout.gridItemState.expanded : false,
                },
                size: `${width}x${height}`,
            });
        });

        const highestI = mapped.reduce((highest, w) => Math.max(highest, w.i), 0);
        unplaced.forEach((w, i) => {
            mapped.push({
                ...w,
                i: highestI + i + 1,
                ...this.findEmptyPosition(mapped),
                type: WIDGET_CODE_TO_TYPE[w.code],
                gridItemState: { active: true, expanded: false },
                size: '0x0',
            });
        });

        return this.sortLayout(mapped);
    }

    layoutPositions(layout) {
        const positions = {};
        layout.forEach(w => {
            positions[w.uniqueIdentifier] = `${w.x},${w.y},${w.w},${w.h}`;
        });
        return positions;
    }

    generateLayoutPositionHash(positions) {
        return JSON.stringify(positions, Object.keys(positions).sort());
    }

    findEmptyPosition(layout) {
        const lowestEmptyPositionByColumn = [0, 0, 0];
        layout.forEach(w => {
            const below = w.y + w.h;
            for (let i = w.x; i < w.x + w.w; i++) {
                lowestEmptyPositionByColumn[i] = Math.max(lowestEmptyPositionByColumn[i], below);
            }
        });
        const lowest = Math.min(...lowestEmptyPositionByColumn);
        const column = lowestEmptyPositionByColumn.indexOf(lowest);
        return {
            x: column,
            y: lowest,
            w: 1,
            h: 4,
        };
    }

    setLayoutTimer = null;

    @Watch('widgetToExpand')
    onWidgetToExpandChanged(toExpand) {
        if (!this.expansion.active) {
            if (toExpand != null) this.expandWidget(toExpand, false);
            return;
        }

        // Expansion is active
        if (toExpand == null) {
            const currentlyExpanded = this.layout.find(w => w.i === this.expansion.widget);
            if (!currentlyExpanded.gridItemState.expanded) this.shrinkWidget();
            return;
        }

        // Expansion is active and a widget is to be expanded, replace.
        this.expandWidget(toExpand, true);
    }

    @Watch('widgetToShrink')
    onWidgetToShrinkChanged(toShrink) {
        if (toShrink == null) return;
        this.shrinkWidget();
    }

    @Watch('locked')
    onLockedChanged(locked) {
        if (!locked && this.expansion.active) {
            // Trigger end of expansion
            this.layout.find(w => w.i === this.expansion.widget).gridItemState.expanded = false;
        }
    }

    expandWidgetMobile(i, isReplace) {
        if (isReplace) {
            this.layout.find(w => w.i === this.expansion.widget).gridItemState.expanded = false;
        } else {
            this.$emit('widget-expansion-start', true);
            this.expansion.active = true;
        }

        this.expansion.scrollY = window.scrollY;
        this.expansion.widget = i;

        const toExpand = this.layout.find(w => w.i === i);
        toExpand.expanded = true;
        toExpand.size = `${toExpand.w}x${toExpand.h}-expand`;

        const idWidget = this.getWidgetRef(toExpand);
        if (idWidget) {
            // Make the widget full screen.
            const element = this.$refs[idWidget];
            element[0].classList.add('vrm-widget__fullscreen');

            // TODO(rick): Find a better way to solve the page being scrollable behind a fullscreen widget on mobile.
            // For now we disable scrolling on the entire site while the widget is fullscreen.
            const html = $('html')[0];
            html.style.overflow = 'hidden';
        } else {
            console.warn('Trying to expand a widget without an id.');
        }
    }

    shrinkWidgetMobile() {
        const widget = this.layout.find(w => w.i === this.expansion.widget);
        widget.expanded = false;
        widget.size = `${widget.w}x${widget.h}`;

        const idWidget = this.getWidgetRef(widget);
        if (idWidget) {
            const element = this.$refs[idWidget];
            element[0].classList.remove('vrm-widget__fullscreen');

            // TODO(rick): Find a better way to solve the page being scrollable behind a fullscreen widget on mobile.
            // For now we disable scrolling on the entire site while the widget is fullscreen.
            const html = $('html')[0];
            html.style.removeProperty('overflow');
        } else {
            console.warn('Trying to shrink a widget without an id.');
        }

        this.expansion.active = false;
        this.expansion.widget = -1;

        this.$emit('widget-expansion-end', false);
    }

    expandWidget(i, isReplace) {
        if (this.isMobile) {
            return this.expandWidgetMobile(i, isReplace);
        }

        if (isReplace) {
            this.layout.find(w => w.i === this.expansion.widget).gridItemState.expanded = false;
        } else {
            this.$emit('widget-expansion-start', true);
            this.expansion.active = true;
        }

        const controls = $('#vrm-advanced-controls')[0].getBoundingClientRect();
        const navbar =
            window.innerWidth < 1280 ? $('.vrm-header')[0].getBoundingClientRect().height : 0;
        const fullHeight = window.innerHeight - controls.height - navbar;
        const rowOffset = Math.floor(fullHeight / ROW_HEIGHT);

        this.expansion.scrollY = window.scrollY;
        this.expansion.widget = i;

        if (isReplace) {
            this.applyLayoutPositions(this.layout);
        } else {
            this.expansion.oldPositions = this.layout.reduce((acc, { i, x, y, w, h }) => {
                acc[i] = { x, y, w, h };
                return acc;
            }, {});
        }

        // Shift all widgets
        this.layout.forEach(w => (w.y += rowOffset));

        // Expand widget
        const toExpand = this.layout.find(w => w.i === i);
        toExpand.x = 0;
        toExpand.y = 0;
        toExpand.w = 3;
        toExpand.h = rowOffset;
        toExpand.size = `${toExpand.w}x${toExpand.h}-expand`;

        // Scroll to top
        window.scrollTo({
            top: window.scrollY + controls.top - navbar + 10,
            behavior: 'smooth',
        });
    }

    shrinkWidget() {
        if (this.isMobile) {
            return this.shrinkWidgetMobile();
        }

        this.applyLayoutPositions(this.layout);

        window.scrollTo({
            top: this.expansion.scrollY,
            behavior: 'smooth',
        });

        let widget = this.layout.find(w => w.i === this.expansion.widget);
        if (widget) widget.size = widget.size.split('-')[0] + '-shrink';

        this.expansion.active = false;
        this.expansion.widget = -1;
        this.$emit('widget-expansion-end', false);
    }

    applyLayoutPositions(layout) {
        layout.forEach(w => {
            const oldPosition = this.expansion.oldPositions[w.i];
            w.x = oldPosition.x;
            w.y = oldPosition.y;
            w.w = oldPosition.w;
            w.h = oldPosition.h;
        });
    }

    get widgetToExpand() {
        const widget = this.layout.find(
            w => w.i !== this.expansion.widget && w.gridItemState.expanded,
        );
        return widget ? widget.i : null;
    }

    get widgetToShrink() {
        const widget = this.layout.find(
            w => w.i === this.expansion.widget && !w.gridItemState.expanded,
        );
        return widget ? widget.i : null;
    }

    get widgetIds() {
        return this.widgets.map(w => w.uniqueIdentifier).sort();
    }

    get widgetsLength() {
        return this.widgets.length;
    }

    get isMobile() {
        return this.breakpoint === BREAKPOINT_SM;
    }

    get gridEditable() {
        return !this.isMobile && !this.locked && !this.expansion.active;
    }

    get hasTouchEnabled() {
        return (
            'ontouchstart' in window ||
            navigator.maxTouchPoints > 0 ||
            navigator.msMaxTouchPoints > 0
        );
    }

    /**
     * Get a reference identifier for a specific widget, so we can reference the dom elements for expanding/shrinking.
     *
     * @param widget
     * @returns {*|string}
     */
    getWidgetRef(widget) {
        return widget.uniqueIdentifier;
    }
}
</script>
